import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Inbox, Loader2, Check, X, Clock, ShoppingBag, PiggyBank,
  AlertTriangle, Package, Edit3, Trash2, CheckCircle2, PartyPopper,
  ShieldCheck, Eye, Landmark,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatGBP, conditionLabel } from "@/lib/marketplace";
import { publishListingUpdate } from "@/lib/listingBus";
import { ListingPhoto } from "@/components/PhotoPlaceholder";
import EditListingModal from "@/components/EditListingModal";
import InspectListingModal, { inspectVariantForOrder, fulfillmentLabel } from "@/components/InspectListingModal";
import { useAuth } from "@/context/AuthContext";

/**
 * UnifiedPendingQueue — Iteration 124.
 *
 * One single card list that surfaces EVERY pending item a parent must
 * decide on:
 *
 *   • Kid-submitted listings waiting for approval
 *   • Kid-edit re-approvals (kid touched a live listing)
 *   • Kid purchase reservations (checkout waiting for parent OK)
 *   • Allowance / top-up requests
 *
 * Every LISTING card renders the same 4-action strip:
 *   [1] Approve   → live
 *   [2] Reject    → back to kid (with optional note)
 *   [3] Edit      → opens full EditListingModal
 *   [4] Delete    → permanently removes (with confirm)
 *
 * Order + allowance cards keep their contextual approve/reject buttons
 * plus a Delete affordance so parents can permanently dismiss stale
 * requests. Purchase/allowance items are NOT editable (there is
 * nothing to edit) so the Edit button is intentionally omitted.
 */

function relativeTime(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const mins = Math.round(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return `${days}d ago`;
  } catch { return ""; }
}

function timeRemaining(iso) {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = then - now;
    if (diff <= 0) return { expired: true, label: "Expired" };
    const hrs = Math.floor(diff / (60 * 60 * 1000));
    const mins = Math.round((diff % (60 * 60 * 1000)) / 60000);
    if (hrs > 0) return { expired: false, label: `${hrs}h ${mins}m left` };
    return { expired: false, label: `${mins}m left` };
  } catch { return null; }
}

export default function UnifiedPendingQueue({ kidsById, onToast }) {
  const { user } = useAuth() || {};
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [flash, setFlash] = useState("");
  const [busyId, setBusyId] = useState("");
  // Iteration 299 — bulk approve: checkbox selection over pending
  // kid-submitted LISTINGS only (orders/allowances/cash-outs keep their
  // own contextual single-tap actions).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  // Iteration 259 — view-only inspection modal state.
  // Shape: { variant, kind: "listing"|"order", id, payload, details }
  const [inspect, setInspect] = useState(null);
  const [rejectPromptId, setRejectPromptId] = useState("");
  const [rejectNote, setRejectNote] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState("");

  const [pendingListings, setPendingListings] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [allowanceRequests, setAllowanceRequests] = useState([]);
  // Iteration 276 — child cash-out (Transfer to Parent) requests.
  const [cashouts, setCashouts] = useState([]);
  const [cashoutPinId, setCashoutPinId] = useState("");
  const [cashoutPin, setCashoutPin] = useState("");
  const [kidEditListings, setKidEditListings] = useState([]);
  // Iteration 212 — track ids currently being deleted (within the 6s
  // undo window OR committed within this session) so a poll re-fetch
  // never re-inserts them. Without this the 45s auto-poll or a manual
  // reload could race with the still-pending DELETE and the row would
  // "come back". Once the DELETE lands, the backend shim filter
  // (iteration 210) also strips it, but this belt-and-braces guard
  // covers the eventual-consistency window and any other card list
  // that shares state.
  const [deletingIds, setDeletingIds] = useState(() => new Set());
  const addDeletingId = useCallback((id) => {
    setDeletingIds((prev) => {
      const next = new Set(prev); next.add(id); return next;
    });
  }, []);
  const removeDeletingId = useCallback((id) => {
    setDeletingIds((prev) => {
      const next = new Set(prev); next.delete(id); return next;
    });
  }, []);

  const kidNameFor = (kid_id) =>
    kidsById?.[kid_id]?.name || kidsById?.[kid_id]?.kid_name || "Your kid";

  const showFlash = useCallback((msg) => {
    setFlash(msg);
    setTimeout(() => setFlash(""), 4500);
  }, []);

  const load = useCallback(async () => {
    setErr("");
    try {
      const [queueRes, approvalsRes, cashoutRes] = await Promise.all([
        api.get("/listings/mod/queue"),
        api.get("/parent/pending-approvals"),
        api.get("/parent/cashout-requests?include_decided=false").catch(() => ({ data: { requests: [] } })),
      ]);
      const queueData = Array.isArray(queueRes.data) ? queueRes.data : [];
      // Dedupe listing queue by id (upstream + shim sometimes double up).
      const seen = new Set();
      const uniqueQueue = [];
      for (const l of queueData) {
        if (!l || !l.id || seen.has(l.id)) continue;
        // Iteration 212 — filter out any id the parent has deleted in
        // this session so a poll race can't re-insert it during the
        // 6s undo window or immediately after commit.
        if (deletingIds.has(l.id)) continue;
        seen.add(l.id);
        uniqueQueue.push(l);
      }
      setPendingListings(uniqueQueue);
      const ap = approvalsRes.data || {};
      setPendingOrders(ap.pending_orders || []);
      setAllowanceRequests(ap.allowance_requests || []);
      setCashouts(cashoutRes?.data?.requests || []);
      const kidEdits = (ap.pending_listings || []).filter(
        (p) => !deletingIds.has(p.listing_id),
      );
      setKidEditListings(kidEdits);
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't load pending items.");
    } finally {
      setLoading(false);
    }
  }, [deletingIds]);

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [load]);

  // Build a single unified list, sorted by pending timestamp (newest first).
  const unified = useMemo(() => {
    const rows = [];
    for (const l of pendingListings) {
      rows.push({
        kind: "listing",
        id: l.id,
        sortTs: l.created_at || l.updated_at || "",
        payload: l,
      });
    }
    for (const e of kidEditListings) {
      // Skip if the listing already appears in the pendingListings queue
      // (they can double up when a kid-edit sits on top of a live listing
      // that upstream still reports as `pending_parent_approval`).
      if (rows.some((r) => r.kind === "listing" && r.id === e.listing_id)) continue;
      rows.push({
        kind: "kid_edit",
        id: e.listing_id,
        sortTs: e.updated_at || e.created_at || "",
        payload: e,
      });
    }
    for (const o of pendingOrders) {
      rows.push({
        kind: "order",
        id: o.id,
        sortTs: o.created_at || "",
        payload: o,
      });
    }
    for (const r of allowanceRequests) {
      rows.push({
        kind: "allowance",
        id: r.id,
        sortTs: r.created_at || "",
        payload: r,
      });
    }
    for (const r of cashouts) {
      rows.push({
        kind: "cashout",
        id: r.id,
        sortTs: r.created_at || "",
        payload: r,
      });
    }
    rows.sort((a, b) => (b.sortTs || "").localeCompare(a.sortTs || ""));
    return rows;
  }, [pendingListings, kidEditListings, pendingOrders, allowanceRequests, cashouts]);

  // ----- Cash-out actions (iteration 276) -----
  // Approve needs the parent's 4-digit household PIN: only then does the
  // backend call upstream `transfer-to-parent` and move the real balance
  // into the verified parent household account.
  const approveCashout = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/parent/cashout-requests/${r.id}/approve`, { pin: cashoutPin });
      setCashouts((rows) => rows.filter((x) => x.id !== r.id));
      setCashoutPinId(""); setCashoutPin("");
      showFlash(`Approved — ${formatGBP((r.amount_pennies || 0) / 100)} moved to your household balance.`);
      onToast?.("Transfer approved.", "success");
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't approve the transfer — check your PIN and try again.");
    } finally {
      setBusyId("");
    }
  };

  const declineCashout = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/parent/cashout-requests/${r.id}/decline`);
      setCashouts((rows) => rows.filter((x) => x.id !== r.id));
      showFlash("Declined — the money stays in your child's wallet.");
      onToast?.("Transfer declined.", "success");
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't decline the transfer.");
    } finally {
      setBusyId("");
    }
  };

  // ----- Listing actions (kid-submitted new toys) -----
  const approveListing = async (l) => {
    setBusyId(l.id);
    try {
      const { data } = await api.post(`/listings/${l.id}/parent-approve`, { approve: true });
      setPendingListings((prev) => prev.filter((q) => q.id !== l.id));
      publishListingUpdate(data || { id: l.id, status: "live" });
      showFlash("Approved — the toy is now live on the shop.");
      onToast?.("Approved.", "success");
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't approve.");
    } finally {
      setBusyId("");
    }
  };

  const rejectListing = async (l, note) => {
    setBusyId(l.id);
    try {
      await api.post(`/listings/${l.id}/parent-approve`, { approve: false, note: note || undefined });
      setPendingListings((prev) => prev.filter((q) => q.id !== l.id));
      publishListingUpdate({ id: l.id, status: "rejected" });
      showFlash("Sent back to your child with your note.");
      onToast?.("Rejected.", "info");
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't reject.");
    } finally {
      setBusyId("");
      setRejectPromptId("");
      setRejectNote("");
    }
  };

  // Iteration 131 — 6-second Undo Delete. Instead of committing the
  // DELETE immediately, we optimistically hide the row and schedule
  // the API call for +6s. A persistent toast counts down and offers
  // an Undo button that clears the timer and re-inserts the row.
  const [undoState, setUndoState] = useState(null);
  // { listing, kind, timerId, restore, countdown }
  const scheduleUndoableDelete = useCallback((l, kind = "listing") => {
    // If another undo is in-flight, commit it first so we don't lose it.
    if (undoState?.timerId) {
      clearTimeout(undoState.timerId);
      undoState.commit?.();
    }
    // Snapshot the row so Undo can restore it exactly.
    const listingSnapshot = { ...l };
    // Iteration 212 — register the id so poll re-fetches skip it.
    addDeletingId(l.id);
    if (kind === "listing") {
      setPendingListings((prev) => prev.filter((q) => q.id !== l.id));
    } else if (kind === "kid_edit") {
      setKidEditListings((prev) => prev.filter((q) => q.listing_id !== l.id));
    }
    let secondsLeft = 6;
    const tick = () => {
      secondsLeft -= 1;
      setUndoState((s) => (s ? { ...s, countdown: secondsLeft } : s));
      if (secondsLeft <= 0) clearInterval(interval);
    };
    const interval = setInterval(tick, 1000);
    const commit = async () => {
      clearInterval(interval);
      try {
        await api.delete(`/listings/${l.id}`);
        publishListingUpdate({ id: l.id, status: "deleted" });
        // Keep the id in `deletingIds` for the rest of the session so
        // a poll during the shim's eventual-consistency window can't
        // resurrect the row.
      } catch (e) {
        setErr(formatApiError(e) || "Couldn't delete.");
        // On failure, restore the row so the parent can retry AND
        // drop the id from `deletingIds` so subsequent polls see it.
        removeDeletingId(l.id);
        if (kind === "listing") setPendingListings((prev) => [listingSnapshot, ...prev]);
        else setKidEditListings((prev) => [listingSnapshot, ...prev]);
      } finally {
        setUndoState(null);
      }
    };
    const timerId = setTimeout(commit, 6000);
    const restore = () => {
      clearTimeout(timerId);
      clearInterval(interval);
      // Iteration 212 — undo also releases the delete guard so a
      // fresh fetch surfaces the row again.
      removeDeletingId(l.id);
      if (kind === "listing") setPendingListings((prev) => [listingSnapshot, ...prev]);
      else setKidEditListings((prev) => [listingSnapshot, ...prev]);
      setUndoState(null);
      onToast?.("Delete cancelled.", "info");
    };
    setUndoState({ timerId, countdown: secondsLeft, restore, commit, id: l.id });
    setConfirmDeleteId("");
  }, [undoState, onToast, addDeletingId, removeDeletingId]);

  const deleteListing = async (l) => {
    // Kept as a thin wrapper so the existing keyboard/API callers still
    // work — routes into the undoable flow.
    scheduleUndoableDelete(l, "listing");
  };

  // ----- Kid-edit re-approval (kid touched a live listing) -----
  const reapproveKidEdit = async (e) => {
    setBusyId(e.listing_id);
    try {
      const { data } = await api.post(`/parent/listings/${e.listing_id}/reapprove-kid-edit`);
      setKidEditListings((prev) => prev.filter((r) => r.listing_id !== e.listing_id));
      publishListingUpdate(data || { id: e.listing_id, awaiting_parent_reapproval: false });
      showFlash("Re-approved — listing is live again.");
      onToast?.("Re-approved.", "success");
    } catch (e2) {
      setErr(formatApiError(e2) || "Couldn't re-approve.");
    } finally {
      setBusyId("");
    }
  };

  // ----- Purchase order / allowance actions -----
  const decideApproval = async (kind, id, approved, note) => {
    setBusyId(id);
    try {
      await api.post(`/parent/approvals/${id}/action`, {
        action: approved ? "approve" : "reject",
        note: note || undefined,
      });
      if (kind === "order") setPendingOrders((rows) => rows.filter((r) => r.id !== id));
      else setAllowanceRequests((rows) => rows.filter((r) => r.id !== id));
      showFlash(approved
        ? (kind === "order" ? "Approved — your kid can grab the toy!" : "Acknowledged — allowance top-up is on you now.")
        : (kind === "order" ? "Released — item is back on the marketplace." : "Dismissed the allowance ask."));
      onToast?.(approved ? "Approved." : "Rejected.", approved ? "success" : "info");
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't save your decision.");
    } finally {
      setBusyId("");
      setRejectPromptId("");
      setRejectNote("");
    }
  };

  // ----- Iteration 259 — view-only inspection modal -----
  const cleanHandle = (h) => String(h || "").replace(/^@+/, "");

  const openInspectListing = (l) => setInspect({
    variant: "new_listing",
    kind: "listing",
    id: l.id,
    payload: l,
    details: {
      badge: "NEW LISTING",
      title: l.title || "Listing",
      photos: Array.isArray(l.photos) ? l.photos : [],
      priceLabel: formatGBP(l.price || 0),
      condition: l.condition,
      description: l.description,
      fulfillmentLabel: fulfillmentLabel(l.fulfillment_method),
      listedByLine: `${l.kid_alias || kidNameFor(l.kid_id)}${l.kid_handle ? ` (@${cleanHandle(l.kid_handle)})` : ""}`,
    },
  });

  const openInspectOrder = (o) => {
    const variant = inspectVariantForOrder(o);
    setInspect({
      variant,
      kind: "order",
      id: o.id,
      payload: o,
      details: {
        badge: variant === "piggy" ? "PIGGY BANK PURCHASE" : variant === "handover" ? "PARENT HAND-OVER" : "SCHOOL COLLECTION",
        title: o.item_title || o.listing_title || "Toy",
        photos: Array.isArray(o.photos) ? o.photos : [],
        priceLabel: formatGBP((o.price_pennies || o.amount_pennies || 0) / 100),
        condition: o.condition,
        description: o.item_description,
        fulfillmentLabel: fulfillmentLabel(o.fulfillment_method),
        buyerLine: `${o.buyer_name || kidNameFor(o.buyer_kid_id || o.kid_id)}${o.buyer_handle ? ` (@${cleanHandle(o.buyer_handle)})` : ""} wants to buy`,
        sellerLine: `${o.seller_name || "Seller"}${o.seller_handle ? ` (@${cleanHandle(o.seller_handle)})` : ""}`,
      },
    });
  };

  const handleInspectApprove = async () => {
    if (!inspect) return;
    const target = inspect;
    if (target.kind === "listing") await approveListing(target.payload);
    else await decideApproval("order", target.id, true);
    setInspect(null);
  };

  const handleInspectReject = async () => {
    if (!inspect) return;
    const target = inspect;
    if (target.kind === "listing") await rejectListing(target.payload, "");
    else await decideApproval("order", target.id, false);
    setInspect(null);
  };

  // ----- Iteration 299 — bulk listing approval -----
  const listingIds = useMemo(() => pendingListings.map((l) => l.id), [pendingListings]);
  const selectedListingIds = useMemo(
    () => listingIds.filter((id) => selectedIds.has(id)),
    [listingIds, selectedIds],
  );
  const allSelected = listingIds.length > 0 && selectedListingIds.length === listingIds.length;

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => setSelectedIds((prev) => (
    prev.size === listingIds.length ? new Set() : new Set(listingIds)
  ));

  const approveSelected = async () => {
    const ids = selectedListingIds;
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/listings/bulk-parent-approve", { ids });
      const okIds = new Set(data?.approved || ids);
      setPendingListings((prev) => prev.filter((q) => !okIds.has(q.id)));
      okIds.forEach((id) => publishListingUpdate({ id, status: "live" }));
      setSelectedIds(new Set());
      showFlash(`Approved ${okIds.size} listing${okIds.size === 1 ? "" : "s"} — now live on the shop.`);
      onToast?.("Bulk approved.", "success");
      if (data?.failed?.length) {
        setErr(`${data.failed.length} listing(s) couldn't be approved — they're still selected for a retry.`);
        setSelectedIds(new Set(data.failed));
      }
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't approve the selected listings.");
    } finally {
      setBulkBusy(false);
    }
  };

  const total = unified.length;

  // Iteration 134 — Family Isolation Pill.
  // Reinforces that the pending queue is scoped strictly to the signed-in
  // parent's household. Uses the parent's display name (fallback: email
  // local-part) so parents get an at-a-glance "yes, this is only my kids"
  // confirmation without ever exposing another household's data.
  const householdLabel = useMemo(() => {
    const raw =
      (user && (user.name || user.parent_name)) ||
      (user && typeof user.email === "string" ? user.email.split("@")[0] : "") ||
      "your";
    return String(raw).trim() || "your";
  }, [user]);
  const pillItemsLabel = `${total} ${total === 1 ? "item" : "items"}`;
  const pillHouseholdLabel = householdLabel === "your"
    ? "your household"
    : `${householdLabel}'s household`;

  // Clean up any pending undo timer on unmount.
  useEffect(() => () => {
    if (undoState?.timerId) clearTimeout(undoState.timerId);
  }, [undoState]);

  return (
    <div className="card-base p-6 md:p-8" data-testid="unified-pending-queue">
      {/* Iteration 131 — 6-second Undo toast for the parent Delete flow. */}
      {undoState && (
        <div
          className="mb-3 rounded-2xl bg-slate-900 text-white px-4 py-3 flex items-center gap-3 shadow-lg"
          role="status"
          data-testid="undo-delete-toast"
        >
          <Trash2 className="w-4 h-4 text-rose-300 flex-shrink-0" />
          <div className="flex-1 min-w-0 text-sm">
            <span className="font-semibold">Listing deleted.</span>{" "}
            <span className="text-slate-300" data-testid="undo-delete-countdown">
              Undoing in {Math.max(0, undoState.countdown)}s.
            </span>
          </div>
          <button
            type="button"
            onClick={undoState.restore}
            className="px-3 py-1.5 rounded-xl bg-white text-slate-900 font-bold text-xs hover:bg-slate-100 transition"
            data-testid="undo-delete-btn"
          >
            Undo ({Math.max(0, undoState.countdown)}s)
          </button>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-brandTeal text-white shadow-sm relative">
            <Inbox className="w-4 h-4" />
            {total > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brandPink text-white text-[10px] font-bold inline-flex items-center justify-center border-2 border-white"
                data-testid="unified-pending-count"
              >
                {total}
              </span>
            )}
          </div>
          <div>
            <h2 className="font-display text-xl md:text-2xl font-medium text-slate-900">Moderation queue</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Listings your kids submitted, waiting for your approval.
            </p>
          </div>
        </div>
        {!loading && (
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brandTeal/8 border border-brandTeal/25 text-brandTeal text-[11px] font-bold whitespace-nowrap"
            title="This queue is strictly scoped to your household — you never see other families' items."
            data-testid="family-isolation-pill"
          >
            <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
            <span>
              Showing{" "}
              <span data-testid="family-isolation-pill-count">{pillItemsLabel}</span>
              {" "}for{" "}
              <span data-testid="family-isolation-pill-household">{pillHouseholdLabel}</span>
            </span>
          </div>
        )}
      </div>

      {flash && (
        <div
          className="mb-3 rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-900 flex items-center gap-2"
          data-testid="unified-pending-flash"
          role="status"
        >
          <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <span className="min-w-0 break-words">{flash}</span>
        </div>
      )}

      {err && (
        <div
          className="mb-3 rounded-2xl bg-rose-50 border border-rose-200 px-4 py-2.5 text-sm text-rose-800 flex items-center gap-2"
          data-testid="unified-pending-error"
        >
          <AlertTriangle className="w-4 h-4" /> {err}
        </div>
      )}

      {loading ? (
        <div
          className="rounded-2xl bg-slate-50 border border-slate-100 px-4 py-6 text-center text-sm text-slate-500 flex items-center justify-center gap-2"
          data-testid="unified-pending-loading"
        >
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : total === 0 ? (
        <div
          className="rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-10 text-center"
          data-testid="unified-pending-empty"
        >
          <div className="mx-auto inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 mb-3">
            <PartyPopper className="w-6 h-6" data-testid="unified-pending-empty-icon" />
          </div>
          <div className="font-display text-lg font-semibold text-emerald-900" data-testid="unified-pending-empty-text">
            Nothing here right now. <span aria-hidden="true">🎉</span>
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="unified-pending-list">
          {listingIds.length >= 2 && (
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/70 px-4 py-2.5" data-testid="bulk-approve-bar">
              <label className="inline-flex items-center gap-2 text-xs font-bold text-emerald-900 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 accent-emerald-600"
                  data-testid="bulk-select-all"
                />
                Select all ({listingIds.length} listings)
              </label>
              <button
                type="button"
                onClick={approveSelected}
                disabled={selectedListingIds.length === 0 || bulkBusy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-sm transition disabled:opacity-50"
                data-testid="bulk-approve-btn"
              >
                {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Approve Selected ({selectedListingIds.length})
              </button>
            </div>
          )}
          {unified.map((row) => {
            if (row.kind === "listing") {
              return (
                <ListingCard
                  key={`listing-${row.id}`}
                  listing={row.payload}
                  busy={busyId === row.id}
                  selected={selectedIds.has(row.id)}
                  onToggleSelect={() => toggleSelect(row.id)}
                  onView={() => openInspectListing(row.payload)}
                  onApprove={() => approveListing(row.payload)}
                  onReject={() => setRejectPromptId(row.id)}
                  onEdit={() => setEditingListing(row.payload)}
                  onDelete={() => setConfirmDeleteId(row.id)}
                  isRejectPrompted={rejectPromptId === row.id}
                  rejectNote={rejectNote}
                  onRejectNoteChange={setRejectNote}
                  onRejectCancel={() => { setRejectPromptId(""); setRejectNote(""); }}
                  onRejectConfirm={() => rejectListing(row.payload, rejectNote)}
                  isDeleteConfirmed={confirmDeleteId === row.id}
                  onDeleteCancel={() => setConfirmDeleteId("")}
                  onDeleteConfirm={() => deleteListing(row.payload)}
                />
              );
            }
            if (row.kind === "kid_edit") {
              return (
                <KidEditCard
                  key={`kid-edit-${row.id}`}
                  entry={row.payload}
                  kidNameFor={kidNameFor}
                  busy={busyId === row.id}
                  onApprove={() => reapproveKidEdit(row.payload)}
                  onReject={() => setRejectPromptId(row.id)}
                  onEdit={() => setEditingListing({
                    id: row.payload.listing_id,
                    title: row.payload.listing_title,
                    description: row.payload.listing_description || "",
                    price: row.payload.listing_price,
                    condition: row.payload.listing_condition,
                    photos: row.payload.listing_thumbnail ? [row.payload.listing_thumbnail] : [],
                  })}
                  onDelete={() => setConfirmDeleteId(row.id)}
                  isRejectPrompted={rejectPromptId === row.id}
                  rejectNote={rejectNote}
                  onRejectNoteChange={setRejectNote}
                  onRejectCancel={() => { setRejectPromptId(""); setRejectNote(""); }}
                  onRejectConfirm={() => rejectListing({ id: row.payload.listing_id }, rejectNote)}
                  isDeleteConfirmed={confirmDeleteId === row.id}
                  onDeleteCancel={() => setConfirmDeleteId("")}
                  onDeleteConfirm={() => deleteListing({ id: row.payload.listing_id })}
                />
              );
            }
            if (row.kind === "order") {
              return (
                <OrderCard
                  key={`order-${row.id}`}
                  order={row.payload}
                  kidNameFor={kidNameFor}
                  busy={busyId === row.id}
                  onView={() => openInspectOrder(row.payload)}
                  onApprove={() => decideApproval("order", row.id, true)}
                  onReject={() => setRejectPromptId(row.id)}
                  onDelete={() => setConfirmDeleteId(row.id)}
                  isRejectPrompted={rejectPromptId === row.id}
                  rejectNote={rejectNote}
                  onRejectNoteChange={setRejectNote}
                  onRejectCancel={() => { setRejectPromptId(""); setRejectNote(""); }}
                  onRejectConfirm={() => decideApproval("order", row.id, false, rejectNote)}
                  isDeleteConfirmed={confirmDeleteId === row.id}
                  onDeleteCancel={() => setConfirmDeleteId("")}
                  onDeleteConfirm={() => decideApproval("order", row.id, false, "Deleted by parent")}
                />
              );
            }
            // Iteration 276 — child cash-out (Transfer to Parent).
            if (row.kind === "cashout") {
              return (
                <CashoutCard
                  key={`cashout-${row.id}`}
                  request={row.payload}
                  busy={busyId === row.id}
                  isPinPrompted={cashoutPinId === row.id}
                  pin={cashoutPin}
                  onPinChange={setCashoutPin}
                  onApproveStart={() => { setCashoutPinId(row.id); setCashoutPin(""); }}
                  onApproveCancel={() => { setCashoutPinId(""); setCashoutPin(""); }}
                  onApproveConfirm={() => approveCashout(row.payload)}
                  onDecline={() => declineCashout(row.payload)}
                />
              );
            }
            // allowance
            return (
              <AllowanceCard
                key={`allowance-${row.id}`}
                request={row.payload}
                kidNameFor={kidNameFor}
                busy={busyId === row.id}
                onApprove={() => decideApproval("request", row.id, true)}
                onReject={() => setRejectPromptId(row.id)}
                onDelete={() => setConfirmDeleteId(row.id)}
                isRejectPrompted={rejectPromptId === row.id}
                rejectNote={rejectNote}
                onRejectNoteChange={setRejectNote}
                onRejectCancel={() => { setRejectPromptId(""); setRejectNote(""); }}
                onRejectConfirm={() => decideApproval("request", row.id, false, rejectNote)}
                isDeleteConfirmed={confirmDeleteId === row.id}
                onDeleteCancel={() => setConfirmDeleteId("")}
                onDeleteConfirm={() => decideApproval("request", row.id, false, "Deleted by parent")}
              />
            );
          })}
        </div>
      )}

      {/* Iteration 259 — view-only inspection modal (4 event variants). */}
      {inspect && (
        <InspectListingModal
          variant={inspect.variant}
          details={inspect.details}
          busy={busyId === inspect.id}
          onClose={() => setInspect(null)}
          onApprove={handleInspectApprove}
          onReject={handleInspectReject}
          onEdit={inspect.kind === "listing"
            ? () => { const l = inspect.payload; setInspect(null); setEditingListing(l); }
            : null}
          onDelete={inspect.kind === "listing"
            ? () => { const l = inspect.payload; setInspect(null); scheduleUndoableDelete(l, "listing"); }
            : null}
        />
      )}

      {editingListing && (
        <EditListingModal
          listing={editingListing}
          onClose={() => setEditingListing(null)}
          onSaved={(updated) => {
            setEditingListing(null);
            // Merge changes back into pending list so the parent sees the
            // updated preview immediately — pending state is preserved.
            setPendingListings((prev) =>
              prev.map((l) => (l.id === updated?.id ? { ...l, ...updated } : l)),
            );
            showFlash("Saved — still in your pending queue for approval.");
            onToast?.("Saved.", "success");
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

function ActionStrip({
  busy,
  onApprove, approveLabel = "Approve",
  onReject,  rejectLabel = "Reject",
  onEdit,
  onDelete,
  onView,
  showEdit = true,
  testIdBase,
}) {
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid={`${testIdBase}-actions`}>
      {onView && (
        <button
          type="button"
          onClick={onView}
          disabled={busy}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-white border border-slate-300 hover:border-brandSlate text-brandSlate hover:bg-slate-50 text-xs font-bold transition disabled:opacity-60"
          data-testid={`${testIdBase}-view`}
          title="View full details (read-only)"
        >
          <Eye className="w-3.5 h-3.5" /> View
        </button>
      )}
      <button
        type="button"
        onClick={onApprove}
        disabled={busy}
        className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-sm transition disabled:opacity-60"
        data-testid={`${testIdBase}-approve`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
        {approveLabel}
      </button>
      <button
        type="button"
        onClick={onReject}
        disabled={busy}
        className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-white border border-rose-200 hover:border-rose-400 text-rose-700 hover:bg-rose-50 text-xs font-bold transition disabled:opacity-60"
        data-testid={`${testIdBase}-reject`}
      >
        <X className="w-3.5 h-3.5" />
        {rejectLabel}
      </button>
      {showEdit && onEdit && (
        <button
          type="button"
          onClick={onEdit}
          disabled={busy}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-brandSlate text-slate-700 hover:bg-slate-50 text-xs font-bold transition disabled:opacity-60"
          data-testid={`${testIdBase}-edit`}
        >
          <Edit3 className="w-3.5 h-3.5" /> Edit
        </button>
      )}
      <button
        type="button"
        onClick={onDelete}
        disabled={busy}
        className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-rose-400 text-rose-700 hover:bg-rose-50 text-xs font-bold transition disabled:opacity-60"
        data-testid={`${testIdBase}-delete`}
      >
        <Trash2 className="w-3.5 h-3.5" /> Delete
      </button>
    </div>
  );
}

function RejectPrompt({ note, onChange, onCancel, onConfirm, busy, testIdBase }) {
  return (
    <div
      className="w-full basis-full mt-3 rounded-2xl border border-rose-200 bg-rose-50/60 p-3"
      data-testid={`${testIdBase}-reject-prompt`}
    >
      <label className="block text-[11px] font-bold uppercase tracking-wider text-rose-800 mb-1">
        Add a note for your child (optional)
      </label>
      <textarea
        rows={2}
        value={note}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. Let's take clearer photos and try again."
        className="w-full rounded-xl border border-rose-200 px-3 py-2 text-sm focus:border-rose-400 outline-none bg-white resize-none"
        data-testid={`${testIdBase}-reject-note`}
      />
      <div className="flex gap-2 mt-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 hover:border-slate-400 text-slate-700 text-xs font-bold"
          data-testid={`${testIdBase}-reject-cancel`}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="px-3 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold disabled:opacity-60"
          data-testid={`${testIdBase}-reject-confirm`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : "Send back to child"}
        </button>
      </div>
    </div>
  );
}

function DeleteConfirm({ onCancel, onConfirm, busy, testIdBase }) {
  return (
    <div
      className="w-full basis-full mt-3 rounded-2xl border border-rose-200 bg-rose-50/60 p-3"
      data-testid={`${testIdBase}-delete-confirm`}
    >
      <div className="text-sm text-rose-900 font-semibold mb-2">
        Permanently delete this item? This can&rsquo;t be undone.
      </div>
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 hover:border-slate-400 text-slate-700 text-xs font-bold"
          data-testid={`${testIdBase}-delete-cancel`}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="px-3 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold disabled:opacity-60"
          data-testid={`${testIdBase}-delete-confirm-btn`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : "Yes, delete"}
        </button>
      </div>
    </div>
  );
}

function ListingCard(props) {
  const { listing: l, busy } = props;
  const testIdBase = `pending-listing-${l.id}`;
  return (
    <div
      className="border border-slate-200 rounded-2xl p-4 flex flex-wrap gap-4 items-center bg-white"
      data-testid={testIdBase}
    >
      {props.onToggleSelect && (
        <input
          type="checkbox"
          checked={!!props.selected}
          onChange={props.onToggleSelect}
          className="w-4 h-4 accent-emerald-600 flex-shrink-0 cursor-pointer"
          aria-label={`Select ${l.title || "listing"} for bulk approval`}
          data-testid={`${testIdBase}-select`}
        />
      )}
      <button
        type="button"
        onClick={props.onView}
        className="flex-shrink-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brandSlate"
        data-testid={`${testIdBase}-view-photo`}
        title="View full details (read-only)"
      >
        <ListingPhoto src={l.photos?.[0]} className="w-20 h-20 rounded-xl object-cover" alt="" />
      </button>
      <div className="flex-1 min-w-[200px]">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-amber-700 mb-0.5">
          Kid-submitted listing
        </div>
        <button
          type="button"
          onClick={props.onView}
          className="font-display text-lg font-medium text-slate-900 hover:text-brandSlate hover:underline text-left flex items-center gap-2 flex-wrap"
          data-testid={`${testIdBase}-view-title`}
          title="View full details (read-only)"
        >
          {l.title}
        </button>
        <div className="text-sm text-slate-500">
          By {l.kid_alias || "your kid"} · {formatGBP(l.price)} · {conditionLabel(l.condition)}
        </div>
      </div>
      <ActionStrip
        busy={busy}
        onView={props.onView}
        onApprove={props.onApprove}
        onReject={props.onReject}
        onEdit={props.onEdit}
        onDelete={props.onDelete}
        testIdBase={testIdBase}
      />
      {props.isRejectPrompted && (
        <RejectPrompt
          note={props.rejectNote}
          onChange={props.onRejectNoteChange}
          onCancel={props.onRejectCancel}
          onConfirm={props.onRejectConfirm}
          busy={busy}
          testIdBase={testIdBase}
        />
      )}
      {props.isDeleteConfirmed && (
        <DeleteConfirm
          onCancel={props.onDeleteCancel}
          onConfirm={props.onDeleteConfirm}
          busy={busy}
          testIdBase={testIdBase}
        />
      )}
    </div>
  );
}

function KidEditCard(props) {
  const { entry: e, busy, kidNameFor } = props;
  const testIdBase = `pending-kid-edit-${e.listing_id}`;
  const pe = e.pending_edit || {};
  const newPricePounds = (typeof pe.price_pennies === "number")
    ? pe.price_pennies / 100
    : (typeof pe.price === "number" ? pe.price : null);
  return (
    <div
      className="rounded-2xl border border-violet-200 bg-violet-50/40 p-4 flex flex-wrap gap-4 items-center"
      data-testid={testIdBase}
    >
      {e.listing_thumbnail && (
        <img src={e.listing_thumbnail} alt="" className="w-20 h-20 rounded-xl object-cover flex-shrink-0 border border-violet-200" />
      )}
      <div className="flex-1 min-w-[200px]">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-violet-700 mb-0.5">
          Kid edit · needs re-approval
        </div>
        <div className="font-display text-lg font-medium text-slate-900">{e.listing_title || "Listing"}</div>
        <div className="text-sm text-slate-500">
          By {e.kid_alias || kidNameFor(e.kid_id)} · {formatGBP(e.listing_price || 0)}
          {pe.title ? <> · Now &ldquo;{pe.title}&rdquo;</> : null}
          {newPricePounds !== null ? <> · New price {formatGBP(newPricePounds)}</> : null}
        </div>
      </div>
      <ActionStrip
        busy={busy}
        approveLabel="Re-approve"
        onApprove={props.onApprove}
        onReject={props.onReject}
        onEdit={props.onEdit}
        onDelete={props.onDelete}
        testIdBase={testIdBase}
      />
      {props.isRejectPrompted && (
        <RejectPrompt
          note={props.rejectNote}
          onChange={props.onRejectNoteChange}
          onCancel={props.onRejectCancel}
          onConfirm={props.onRejectConfirm}
          busy={busy}
          testIdBase={testIdBase}
        />
      )}
      {props.isDeleteConfirmed && (
        <DeleteConfirm
          onCancel={props.onDeleteCancel}
          onConfirm={props.onDeleteConfirm}
          busy={busy}
          testIdBase={testIdBase}
        />
      )}
    </div>
  );
}

function OrderCard(props) {
  const { order: o, busy, kidNameFor } = props;
  const testIdBase = `pending-order-${o.id}`;
  const remaining = timeRemaining(o.expires_at);
  const priceLabel = formatGBP((o.price_pennies || o.amount_pennies || 0) / 100);
  const buyerName = o.buyer_name || kidNameFor(o.buyer_kid_id || o.kid_id);
  const sellerName = o.seller_name || "Seller";
  const itemTitle = o.item_title || o.listing_title || "Toy";
  return (
    <div
      className="rounded-2xl border border-brandPink/25 bg-gradient-to-br from-brandPink/6 via-white to-transparent p-4"
      data-testid={testIdBase}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-brandPink/15 text-brandPink flex-shrink-0">
          <ShoppingBag className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-brandPink">
            Reservation · Kid Purchase
          </div>
          <div className="font-bold text-slate-900 leading-tight">
            {buyerName} wants to buy <button type="button" onClick={props.onView} className="italic hover:text-brandPink hover:underline" data-testid={`${testIdBase}-view-title`} title="View full details (read-only)">&ldquo;{itemTitle}&rdquo;</button> from {sellerName} for {priceLabel}
          </div>
          <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span>Sent {relativeTime(o.created_at)}</span>
            {remaining && (
              <span className={`inline-flex items-center gap-1 font-bold ${remaining.expired ? "text-rose-700" : "text-amber-700"}`}>
                <Clock className="w-3 h-3" /> {remaining.label}
              </span>
            )}
          </div>
        </div>
        <ActionStrip
          busy={busy}
          approveLabel="Approve"
          rejectLabel="Release"
          onView={props.onView}
          onApprove={props.onApprove}
          onReject={props.onReject}
          onEdit={null}
          onDelete={props.onDelete}
          showEdit={false}
          testIdBase={testIdBase}
        />
        {props.isRejectPrompted && (
          <RejectPrompt
            note={props.rejectNote}
            onChange={props.onRejectNoteChange}
            onCancel={props.onRejectCancel}
            onConfirm={props.onRejectConfirm}
            busy={busy}
            testIdBase={testIdBase}
          />
        )}
        {props.isDeleteConfirmed && (
          <DeleteConfirm
            onCancel={props.onDeleteCancel}
            onConfirm={props.onDeleteConfirm}
            busy={busy}
            testIdBase={testIdBase}
          />
        )}
      </div>
    </div>
  );
}

// Iteration 276 — child cash-out request card. Approve requires the
// parent's 4-digit household PIN (banking safety: funds move only into
// the verified parent household account). Iteration 277 — exported so
// the Ledger → Pending Approvals inbox renders the identical card.
export function CashoutCard({
  request, busy, isPinPrompted, pin, onPinChange,
  onApproveStart, onApproveCancel, onApproveConfirm, onDecline,
}) {
  const r = request;
  const amountLabel = formatGBP((r.amount_pennies || 0) / 100);
  const kidLabel = r.kid_name || "Your child";
  const handle = r.kid_handle ? ` (@${String(r.kid_handle).replace(/^@/, "")})` : "";
  return (
    <div
      className="rounded-2xl border border-emerald-300/60 bg-gradient-to-br from-emerald-50 via-white to-transparent p-4"
      data-testid={`cashout-card-${r.id}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 flex-shrink-0">
          <Landmark className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-700">
            Cash-Out Request
          </div>
          <div className="font-bold text-slate-900 leading-tight">
            {kidLabel}{handle} wants to transfer {amountLabel} to your household balance
          </div>
          <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span>Sent {relativeTime(r.created_at)}</span>
            <span>· Household Balance Transfer</span>
          </div>
        </div>
        <div className="flex flex-col gap-2 items-stretch ml-auto w-full sm:w-auto">
          {!isPinPrompted ? (
            <>
              <button type="button" disabled={busy} onClick={onApproveStart}
                className="btn-primary !py-2 !px-4 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid={`cashout-approve-${r.id}`}>
                <Check className="w-3.5 h-3.5" /> Approve
              </button>
              <button type="button" disabled={busy} onClick={onDecline}
                className="px-4 py-2 rounded-full text-xs font-bold border border-slate-200 text-slate-600 hover:border-rose-300 hover:text-rose-600 transition disabled:opacity-50"
                data-testid={`cashout-decline-${r.id}`}>
                Decline
              </button>
            </>
          ) : (
            <div className="rounded-xl border border-emerald-200 bg-white p-3 space-y-2" data-testid={`cashout-pin-form-${r.id}`}>
              <label className="block text-[11px] font-bold text-slate-600" htmlFor={`cashout-pin-${r.id}`}>
                Enter your 4-digit parent PIN to approve
              </label>
              <input
                id={`cashout-pin-${r.id}`}
                type="password" inputMode="numeric" maxLength={4}
                className="input-base !py-2 text-center tracking-[0.4em] font-mono"
                placeholder="••••"
                value={pin}
                onChange={(e) => onPinChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
                data-testid={`cashout-pin-${r.id}`}
              />
              <div className="flex gap-2">
                <button type="button" disabled={busy || pin.length !== 4} onClick={onApproveConfirm}
                  className="btn-primary !py-2 !px-4 text-xs flex-1 disabled:opacity-50"
                  data-testid={`cashout-approve-confirm-${r.id}`}>
                  {busy ? "Moving…" : `Confirm ${amountLabel}`}
                </button>
                <button type="button" disabled={busy} onClick={onApproveCancel}
                  className="px-3 py-2 rounded-full text-xs font-bold border border-slate-200 text-slate-500"
                  data-testid={`cashout-approve-cancel-${r.id}`}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AllowanceCard(props) {
  const { request: r, busy, kidNameFor } = props;
  const testIdBase = `pending-allowance-${r.id}`;
  return (
    <div
      className="rounded-2xl border border-brandTeal/25 bg-gradient-to-br from-brandTeal/6 via-white to-transparent p-4"
      data-testid={testIdBase}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-brandTeal/15 text-brandTeal flex-shrink-0">
          <PiggyBank className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-brandTeal">
            Allowance Request
          </div>
          <div className="font-bold text-slate-900 leading-tight">
            {kidNameFor(r.kid_id)} asked for {formatGBP((r.amount_pennies || 0) / 100)}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">Asked {relativeTime(r.created_at)}</div>
        </div>
        <ActionStrip
          busy={busy}
          approveLabel="Acknowledge"
          rejectLabel="Dismiss"
          onApprove={props.onApprove}
          onReject={props.onReject}
          onEdit={null}
          onDelete={props.onDelete}
          showEdit={false}
          testIdBase={testIdBase}
        />
        {props.isRejectPrompted && (
          <RejectPrompt
            note={props.rejectNote}
            onChange={props.onRejectNoteChange}
            onCancel={props.onRejectCancel}
            onConfirm={props.onRejectConfirm}
            busy={busy}
            testIdBase={testIdBase}
          />
        )}
        {props.isDeleteConfirmed && (
          <DeleteConfirm
            onCancel={props.onDeleteCancel}
            onConfirm={props.onDeleteConfirm}
            busy={busy}
            testIdBase={testIdBase}
          />
        )}
      </div>
    </div>
  );
}
